const { buildConnection } = require('./connection.js');
const { sanitiseDid } = require('./did.js');
const { CBDCVCType } = require('./utils.js');
const utils = require('./utils.js');
const { signatureVerify, blake2AsHex } = require('@polkadot/util-crypto');

/**
 * Get account balance(Highest Form) based on the did supplied.
 * @param {String} did Identifier of the user
 * @param {ApiPromse=} api Api Object from Build Connection
 * @returns {String} Balance In Highest Form
 * @example await getBalanceFromDID(did, api)
 */
const getBalance = async (did, api = false) => {
  // Resolve the did to get account ID
  try {
    const provider = api || await buildConnection('local');
    const did_hex = sanitiseDid(did);
    const accountInfo = await provider.query.did.account(did_hex);
    const { data } = accountInfo.toJSON();
    console.log("Data:", data);
    return data.free / 1e6;
  } catch (err) {
    // console.log(err);
    return null;
  }
};
/**
 * Listen to balance changes for a DID and execute the callback.
 * @param {String} identifier DID
 * @param {Function} callback Cb function to execute with new balance in Highest Form
 * @param {ApiPromise=} api Api Object from Build Connection
 */
const subscribeToBalanceChanges = async (identifier, callback, api = false) => {
  try {
    const provider = api || await buildConnection('local');
    const did_hex = sanitiseDid(identifier);
    return provider.query.did.account(did_hex, ({ data: { free: currentBalance } }) => {
      callback(currentBalance.toNumber() / 1e6);
    });
  } catch (err) {
    return null;
  }
};

/**
 * Create CBDC VC
 * @param  {Object} params Parameters for the CBDC VC
 * @param  {String} params.vcType - Type of VC (from VCType enum)
 * @param  {String} params.currencyCode - Currency code (e.g., 'USD', 'EUR')
 * @param  {Number} params.amount - Amount of currency
 * @param  {String} [params.receiverDid] - DID of the receiver (required for TokenTransferVC)
 * @param  {String} owner - DID of the owner
 * @param  {Array<String>} issuers - Array of issuer DIDs
 * @param  {KeyPair} sigKeypair - DID Owner Key Ring pair
 * @returns {String} VC Hex String
 */
function generateCBDCVC({ vcType, currencyCode, amount, receiverDid }, owner, issuers, sigKeypair) {
  let encodedVCProperty, encodedData, hash;

  // Sanitize owner and issuers DIDs
  owner = sanitiseDid(owner);
  issuers = issuers.map(issuer => sanitiseDid(issuer));

  // Prepare VC property based on VC type
  if (vcType === CBDCVCType.MintTokens || vcType === CBDCVCType.SlashTokens) {
    console.log("MintTokens");
    // For Mint or Slash tokens
    const vcProperty = {
      amount: amount,
      currency_code: utils.encodeData(currencyCode.padEnd(utils.CURRENCY_CODE_BYTES, '\0'), 'currency_code')
    };

    encodedVCProperty = utils.encodeData(vcProperty, 'MintSlashProperty')
      .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');
  }
  else if (vcType === CBDCVCType.TokenTransferVC) {
    // For Token Transfer VC
    if (!receiverDid) {
      throw new Error('Receiver DID is required for TokenTransferVC');
    }

    const vcProperty = {
      receiver_did: sanitiseDid(receiverDid),
      amount: amount,
      currency_code: utils.encodeData(currencyCode.padEnd(utils.CURRENCY_CODE_BYTES, '\0'), 'currency_code'),
    };

    encodedVCProperty = utils.encodeData(vcProperty, 'DistributeTokenProperty')
      .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');
  }
  else if (vcType === CBDCVCType.GenericVC && params.cid) {
    // For Generic VC type
    const vcProperty = {
      cid: utils.encodeData(params.cid.padEnd(CID_BYTES, '\0'), 'CID')
    };

    encodedVCProperty = utils.encodeData(vcProperty, 'GenericVC')
      .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');
  }
  else if (vcType === CBDCVCType.TokenVC) {
    // For Token VC (creating a new token)
    if (!params.tokenName || !params.decimal) {
      throw new Error('Token name and decimal are required for TokenVC');
    }

    const vcProperty = {
      token_name: utils.encodeData(params.tokenName.padEnd(TOKEN_NAME_BYTES, '\0'), 'token_bytes'),
      reservable_balance: params.reservableBalance || 0,
      decimal: params.decimal,
      currency_code: utils.encodeData(currencyCode.padEnd(CURRENCY_CODE_BYTES, '\0'), 'currency_code')
    };

    encodedVCProperty = utils.encodeData(vcProperty, 'TokenVC')
      .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');
  }
  else {
    throw new Error(`Unsupported VC type: ${vcType}`);
  }

  // Create encoded data
  encodedData = utils.encodeData({
    vc_type: vcType,
    vc_property: encodedVCProperty,
    owner,
    issuers
  }, 'VC_HEX');

  // Generate hash
  hash = blake2AsHex(encodedData);

  // Sign the hash
  const sign = utils.bytesToHex(sigKeypair.sign(hash));

  // Create VC object
  const vcObject = {
    hash,
    owner,
    signatures: [sign],
    vc_property: encodedVCProperty,
    vc_type: vcType,
    issuers
  };

  // Encode and return
  return utils.encodeData(vcObject, 'CBDCVC');
}



module.exports = {
  getBalance,
  generateCBDCVC,
  subscribeToBalanceChanges,
};
